// THE UNITS OF A SEARCH ANSWER — the pieces it is ticked, rendered and laid out by
// (busy-my-web#682, the final web-search design Pouria approved, Figma 40015125:58088).
//
// An answer used to be split on every newline: one line, one tickable chunk. That was right while
// the answer was only paragraphs, bullets and headings, but the final design asks Gemini for pipe
// TABLES (prices, specs, rankings), FENCED CODE and a `>` CALLOUT (the health "get help now" box) —
// all of them several lines that only mean anything together. Split per line, a table became a
// stack of loose "| 256 GB | 149.999 TL |" rows and a code block lost its fence, each drawn (and
// saved) on its own. So the unit is now: one line, OR one whole table / code block / callout.
//
// The same units decide the page's ORDER (#682 "Page order for a WEB SEARCH turn"): the opening
// paragraph comes first, then the typed blocks (products, pictures, videos, places), then the rest
// of the answer. `openerCount` / `splitOpener` are that rule, for the finished chunks and for the
// still-streaming text alike — so a turn cannot finish in a different order from the one it
// streamed in. Pure: no store, no DOM. Gemini's words are never changed here, only grouped.

import { answerLineText, inlineMarkdownToText, firstLinkUrl } from '@/lib/answerLineText'

const FENCE = /^\s*(```|~~~)/
const TABLE_ROW = /^\s*\|/
const QUOTE = /^\s*>/
const HEADING = /^\s*#{1,6}\s+/

/** A GFM table's separator row ("|---|:--:|"): only pipes, dashes, colons and spaces. */
export const isTableSeparator = (line) => {
  const s = String(line ?? '')
  return /^[\s|:-]+$/.test(s) && s.includes('-') && s.includes('|')
}

/** Does a table start on line `i`: a pipe row with a separator row under it. */
const isTableAt = (lines, i) => TABLE_ROW.test(lines[i]) && isTableSeparator(lines[i + 1])

/** One pipe row → its cells, trimmed. An escaped `\|` stays inside its cell. */
export const tableCells = (line) => {
  let s = String(line ?? '').trim()
  if (s.startsWith('|')) s = s.slice(1)
  if (s.endsWith('|') && !s.endsWith('\\|')) s = s.slice(0, -1)
  return s.split(/(?<!\\)\|/).map(c => c.trim().replace(/\\\|/g, '|'))
}

/** Is this unit a heading ("### Key Specifications")? Headings never carry a chip and never end the opener. */
export const isHeadingMd = (md) => HEADING.test(String(md ?? ''))

/**
 * The answer as units, with where each sits in the text: `{ md, kind, start, end, closed }`.
 * `kind` is 'line' | 'code' | 'table' | 'quote'; `start`/`end` are offsets into `md` (end is the end
 * of the unit's last line, before its newline); `closed` is false only for a code fence that has
 * not been closed yet — which is what a block still being streamed looks like. A one-line unit is
 * trimmed (as the per-line split always was); a block keeps its lines as written, because a code
 * block's indentation IS its content.
 *
 * @param {string} md
 * @returns {Array<{md: string, kind: string, start: number, end: number, closed: boolean}>}
 */
export function answerUnitSpans(md) {
  const src = String(md ?? '')
  const lines = src.split('\n')
  const starts = []
  let off = 0
  for (const l of lines) { starts.push(off); off += l.length + 1 }
  const spans = []
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    if (!line.trim()) continue
    let j = i
    let kind = 'line'
    let closed = true
    if (FENCE.test(line)) {
      kind = 'code'
      const marker = line.trim().slice(0, 3)
      j = i + 1
      while (j < lines.length && !lines[j].trim().startsWith(marker)) j++
      if (j >= lines.length) { j = lines.length - 1; closed = false }
    } else if (isTableAt(lines, i)) {
      kind = 'table'
      while (j + 1 < lines.length && TABLE_ROW.test(lines[j + 1])) j++
    } else if (QUOTE.test(line)) {
      kind = 'quote'
      while (j + 1 < lines.length && QUOTE.test(lines[j + 1])) j++
    }
    const text = kind === 'line' ? line.trim() : lines.slice(i, j + 1).join('\n')
    spans.push({ md: text, kind, start: starts[i], end: starts[j] + lines[j].length, closed })
    i = j
  }
  return spans
}

/** The code inside a fenced unit, without its fences; and its language ('' when none is named). */
export const codeOf = (md) => {
  const lines = String(md ?? '').split('\n')
  const lang = (lines[0] || '').trim().replace(/^(```|~~~)/, '').trim()
  const body = lines.slice(1)
  if (body.length && FENCE.test(body[body.length - 1])) body.pop()
  return { lang, code: body.join('\n') }
}

/** A callout's lines with their `>` marks taken off (blank inner lines dropped). */
export const quoteLines = (md) => String(md ?? '').split('\n')
  .map(l => l.replace(/^\s*>\s?/, '').trim())
  .filter(Boolean)

/**
 * What one unit is SAVED as when it is ticked (lib/answerLineText's rule, per kind): a line reads as
 * its words; a table as one line per row, cells joined by " · "; a callout as its lines; code as the
 * code itself, character for character. `url` is the first page the unit links to.
 *
 * @param {{md: string, kind?: string}} unit
 * @returns {{ text: string, url: string }}
 */
export function unitText(unit) {
  const md = String(unit?.md ?? '')
  if (unit?.kind === 'code') return { text: codeOf(md).code, url: '' }
  if (unit?.kind === 'table') {
    const rows = md.split('\n').filter(l => !isTableSeparator(l))
    return {
      text: rows.map(r => tableCells(r).map(inlineMarkdownToText).filter(Boolean).join(' · ')).filter(Boolean).join('\n'),
      url: firstLinkUrl(md),
    }
  }
  if (unit?.kind === 'quote') {
    return { text: quoteLines(md).map(l => answerLineText(l).text).filter(Boolean).join('\n'), url: firstLinkUrl(md) }
  }
  return answerLineText(md)
}

/**
 * The answer as the units it is ticked by — `{ md, kind, text, url }` each. This replaces the old
 * one-line-per-chunk split in the research store (busy-my-web#682), so a table or a code block is
 * ONE tickable chunk and is never cut into rows that render broken.
 *
 * @param {string} md
 * @returns {Array<{md: string, kind: string, text: string, url: string}>}
 */
export const answerUnits = (md) => answerUnitSpans(md).map(s => ({ md: s.md, kind: s.kind, ...unitText(s) }))

/**
 * How many of an answer's leading units make its OPENING (#682 step 3): any headings it starts with,
 * then its first real unit — a paragraph, a list item, a table or the health callout. Everything
 * after it is "the rest", drawn below the typed blocks.
 *
 * @param {string[]} mds the units' markdown, in order
 * @returns {number}
 */
export function openerCount(mds) {
  const list = Array.isArray(mds) ? mds : []
  let k = 0
  while (k < list.length && isHeadingMd(list[k])) k++
  return Math.min(list.length, k + 1)
}

/**
 * The STREAMING answer cut the same way: `{ opener, rest, ended }`. `ended` says the opening unit is
 * finished — the line has its newline, the code block its closing fence, the table or callout a
 * blank line (or something after it) — and only then do the typed blocks slot in under it; before
 * that the workspace keeps their placeholders there, so the opener never jumps down the page
 * (#682: "While streaming, render progressively; the blocks slot in after the first paragraph once
 * it has ended"). The two halves are slices of the text as received — nothing is rejoined.
 *
 * @param {string} md the answer so far
 * @returns {{ opener: string, rest: string, ended: boolean }}
 */
export function splitOpener(md) {
  const src = String(md ?? '')
  const spans = answerUnitSpans(src)
  if (!spans.length) return { opener: src, rest: '', ended: false }
  const k = openerCount(spans.map(s => s.md))
  const last = spans[k - 1]
  const rest = src.slice(last.end)
  let ended = spans.length > k
  if (!ended) {
    // Only headings so far: the opener's paragraph has not started, so it cannot have ended.
    if (isHeadingMd(last.md)) ended = false
    else if (last.kind === 'code') ended = last.closed
    else if (last.kind === 'line') ended = rest.includes('\n')
    else ended = /\n\s*\n/.test(rest)
  }
  return { opener: src.slice(0, last.end), rest, ended }
}

/**
 * What a research turn draws, in order (busy-my-web#682): a list of sections, each
 * `{ key, kind: 'stream'|'lines'|'blocks', md?, chunks?, caret?, ready? }`.
 *
 * A WEB SEARCH turn is opener → blocks → rest. While it streams, the opener and the rest are
 * slices of the text (`splitOpener`), the caret rides on whichever is being written, and the blocks
 * are `ready` only once the opener has ended. Once finished, they are the turn's tickable chunks cut
 * at `openerCount`. A finished turn with text but no chunks (a stopped stream, a limit notice) still
 * shows its text, as the opener.
 *
 * A CHAT turn (#397) is exactly what it always drew: the streamed prose while it writes, the
 * tickable lines once it is done — no blocks, no reordering.
 *
 * @param {object} turn the research turn
 * @param {{ chat?: boolean }} [opts]
 * @returns {object[]}
 */
export function turnSections(turn, { chat = false } = {}) {
  const chunks = Array.isArray(turn?.answerChunks) ? turn.answerChunks : []
  const answer = String(turn?.answer || '')
  if (chat) {
    const out = []
    if (turn?.streaming && answer) out.push({ key: 'stream', kind: 'stream', md: answer, caret: true })
    if (chunks.length) out.push({ key: 'lines', kind: 'lines', chunks })
    return out
  }
  if (turn?.streaming && answer) {
    const { opener, rest, ended } = splitOpener(answer)
    const hasRest = !!rest.trim()
    return [
      { key: 'opener', kind: 'stream', md: opener, caret: !hasRest },
      { key: 'blocks', kind: 'blocks', ready: ended },
      ...(hasRest ? [{ key: 'rest', kind: 'stream', md: rest, caret: true }] : []),
    ]
  }
  if (chunks.length) {
    const k = openerCount(chunks.map(c => c?.md))
    return [
      { key: 'opener', kind: 'lines', chunks: chunks.slice(0, k) },
      { key: 'blocks', kind: 'blocks', ready: true },
      ...(chunks.length > k ? [{ key: 'rest', kind: 'lines', chunks: chunks.slice(k) }] : []),
    ]
  }
  return [
    ...(answer ? [{ key: 'opener', kind: 'stream', md: answer, caret: false }] : []),
    { key: 'blocks', kind: 'blocks', ready: true },
  ]
}
